import { memo, useLayoutEffect, useRef, useState } from 'react'
import { isSupportedImageData, resolveText } from '../templateDocument'
import { getLayerName, getLayerRole } from '../templateLayers'
import { loadTemplateFonts } from '../templateFonts'

const TYPE_NAMES = { text: 'Bloco de texto', image: 'Imagem', line: 'Linha' }
const CORNERS = ['nw', 'ne', 'sw', 'se']

function TemplateElement({
  element,
  selected = false,
  preview = false,
  values = {},
  onInteractionStart,
  onSelect,
}) {
  const textRef = useRef(null)
  const [overflow, setOverflow] = useState(false)
  const [fontError, setFontError] = useState(false)
  const renderedText = resolveText(element.text, values)

  useLayoutEffect(() => {
    const node = textRef.current
    if (!node || element.type !== 'text') {
      setOverflow(false)
      setFontError(false)
      return undefined
    }
    let active = true
    let fontReady = false
    setFontError(false)
    setOverflow(false)
    const measure = () => {
      if (!active || !fontReady) return
      setOverflow(
        node.scrollHeight > node.clientHeight + 1 ||
          node.scrollWidth > node.clientWidth + 1,
      )
    }
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    const fonts = globalThis.document?.fonts
    fonts?.addEventListener('loadingdone', measure)
    loadTemplateFonts([{
      type: element.type,
      fontFamily: element.fontFamily,
      bold: element.bold,
      italic: element.italic,
    }])
      .then(() => {
        if (!active) return
        fontReady = true
        measure()
        observer?.observe(node)
      })
      .catch(() => {
        if (active) setFontError(true)
      })
    return () => {
      active = false
      observer?.disconnect()
      fonts?.removeEventListener('loadingdone', measure)
    }
  }, [
    element.type,
    element.width,
    element.height,
    element.fontFamily,
    element.fontSize,
    element.bold,
    element.italic,
    element.lineHeight,
    element.alignment,
    renderedText,
  ])

  const startInteraction = (event, mode = 'move', corner = null) => {
    if (preview) return
    onInteractionStart?.(event, element, mode, corner)
  }
  const classes = [
    'lt-element',
    `lt-element-${element.type}`,
    selected && !preview ? 'lt-element-selected' : '',
    element.locked ? 'lt-element-locked' : '',
    overflow ? 'lt-element-overflow' : '',
    fontError ? 'lt-element-font-error' : '',
    preview ? 'lt-element-preview' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      className={classes}
      data-element-id={element.id}
      data-element-type={element.type}
      data-layer-role={getLayerRole(element)}
      data-overflow={overflow ? 'true' : 'false'}
      data-font-error={fontError ? 'true' : 'false'}
      data-locked={element.locked ? 'true' : 'false'}
      role={preview ? undefined : 'group'}
      tabIndex={preview ? undefined : 0}
      aria-label={
        preview
          ? undefined
          : `${getLayerName(element)}${element.locked ? ' (bloqueada)' : ''}`
      }
      title={
        !preview && fontError
          ? `Não foi possível carregar a fonte ${element.fontFamily}. Recarregue o editor antes de exportar.`
          : !preview && overflow
            ? 'O texto ultrapassa a altura do bloco. Aumente o bloco ou reduza o texto.'
          : undefined
      }
      onPointerDown={startInteraction}
      onFocus={() => {
        if (!preview) onSelect?.(element.id)
      }}
      onDragStart={(event) => event.preventDefault()}
      style={{
        position: 'absolute',
        left: `${element.x}mm`,
        top: `${element.y}mm`,
        width: `${element.width}mm`,
        height: `${element.height}mm`,
        boxSizing: 'border-box',
        touchAction: preview ? 'auto' : 'none',
        userSelect: preview ? 'text' : 'none',
        cursor: preview ? 'auto' : element.locked ? 'default' : 'move',
      }}
    >
      {element.type === 'text' && (
        <div
          ref={textRef}
          className="lt-element-content lt-element-text-content"
          style={{
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            boxSizing: 'border-box',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            fontFamily: `"${element.fontFamily}", ${element.fontFamily === 'Times New Roman' ? 'serif' : 'sans-serif'}`,
            fontSize: `${element.fontSize}pt`,
            fontWeight: element.bold ? 700 : 400,
            fontStyle: element.italic ? 'italic' : 'normal',
            textDecoration: element.underline ? 'underline' : 'none',
            color: element.color,
            textAlign: element.alignment,
            lineHeight: element.lineHeight,
          }}
        >
          {renderedText ||
            (!preview && (
              <span className="lt-element-placeholder">Texto vazio</span>
            ))}
        </div>
      )}
      {element.type === 'image' &&
        (isSupportedImageData(element.imageData) ? (
          <img
            className="lt-element-image-content"
            src={element.imageData}
            alt=""
            draggable={false}
            style={{
              width: '100%',
              height: '100%',
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              display: 'block',
              pointerEvents: 'none',
            }}
          />
        ) : (
          !preview && (
            <div className="lt-image-placeholder">Adicione uma imagem</div>
          )
        ))}
      {element.type === 'line' && (
        <div
          className="lt-element-line-content"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '50%',
            borderTop: `1pt solid ${element.color}`,
            pointerEvents: 'none',
          }}
        />
      )}
      {!preview && (overflow || fontError) && (
        <span className="lt-overflow-indicator" aria-hidden="true">
          {fontError ? 'Fonte indisponível' : 'Texto excedente'}
        </span>
      )}
      {selected &&
        !preview &&
        !element.locked &&
        CORNERS.map((corner) => (
          <button
            key={corner}
            type="button"
            className={`lt-resize-handle lt-resize-${corner}`}
            data-lt-resize-handle={corner}
            aria-label={`Redimensionar ${TYPE_NAMES[element.type].toLowerCase()} (${corner})`}
            onPointerDown={(event) => startInteraction(event, 'resize', corner)}
            style={{
              position: 'absolute',
              width: 10,
              height: 10,
              padding: 0,
              left: corner.includes('w') ? 0 : undefined,
              right: corner.includes('e') ? 0 : undefined,
              top: corner.includes('n') ? 0 : undefined,
              bottom: corner.includes('s') ? 0 : undefined,
              transform: `translate(${corner.includes('w') ? '-50%' : '50%'}, ${corner.includes('n') ? '-50%' : '50%'})`,
              cursor:
                corner === 'nw' || corner === 'se'
                  ? 'nwse-resize'
                  : 'nesw-resize',
              touchAction: 'none',
            }}
          />
        ))}
    </div>
  )
}

export default memo(TemplateElement)
